import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Row, Col, Input, Select, InputNumber } from "antd";
import { useSelector } from "react-redux";
import { useMapManager } from "../hooks/useMapManager";

import ButtonStandard from "../../../../../components/utils/button";
import { COLOR_OPTIONS, AREA_TYPE_OPTIONS } from "../config/constants";

import { getPicketIndexDropDown } from "../../../../../services/picketService";
import { getOnlyActives } from "../../../../../services/retreatService";
import { logEventError } from "../../../../../utils/logger";

const { Option } = Select;
const { TextArea } = Input;

const AreaForm = ({
  area,
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
  handleSubmit,
  onCancel,
  onColorChange,
  onRedrawPolygon,
  isSubmitting,
  translation,
}) => {
  const [pickets, setPickets] = useState([]);
  const [isLoadingPickets, setIsLoadingPickets] = useState(false);
  const [retreats, setRetreats] = useState([]);
  const [isLoadingRetreats, setIsLoadingRetreats] = useState(false);

  const { isEditMode } = useMapManager();

  const {
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);

  const loadPickets = useCallback(async () => {
    setIsLoadingPickets(true);
    try {
      let results;

      // Carregar todos os piquetes e filtrar por status
      const response = await getPicketIndexDropDown({
        groupId,
        farmId,
      });
      results = response.data.results;

      // Filtrar piquetes com status Pasturage, Break, Active
      results = results.filter(
        (picket) => picket.status !== "Inactive" || picket.status !== "I"
      );

      setPickets(results);
    } catch (error) {
      logEventError("Erro ao carregar piquetes:", error);
    } finally {
      setIsLoadingPickets(false);
    }
  }, [groupId, farmId]);

  const loadRetreats = useCallback(async () => {
    setIsLoadingRetreats(true);
    try {
      const {
        data: { results },
      } = await getOnlyActives({
        groupId,
        farmId,
      });

      setRetreats(results);
    } catch (error) {
      logEventError("Erro ao carregar retiros:", error);
    } finally {
      setIsLoadingRetreats(false);
    }
  }, [groupId, farmId]);

  const handleColorChange = useCallback(
    (color) => {
      setFieldValue("color", color);
      onColorChange(color);
    },
    [setFieldValue, onColorChange]
  );

  const handleRetreatChange = useCallback(
    (retreatId) => {
      setFieldValue("retreatId", retreatId);
      // Limpar seleção de piquete quando retiro for alterado
      setFieldValue("picketId", null);
    },
    [setFieldValue]
  );

  const filterPickets = useMemo(() => {
    if (values.retreatId === null || values.retreatId === undefined)
      return pickets;
    const retreatPicketIds = retreats
      .find((retreat) => retreat.id === values.retreatId)
      ?.pickets.map((picket) => picket.id);
    return pickets.filter((picket) => retreatPicketIds.includes(picket.id));
  }, [values.retreatId, pickets, retreats]);

  useEffect(() => {
    if (groupId && farmId) {
      loadPickets();
      loadRetreats();
    }
  }, [groupId, farmId, loadPickets, loadRetreats]);

  const isSaveButtonDisabled = false;

  return (
    <div style={{ padding: "16px" }}>
      <h3>
        {isEditMode
          ? translation.areaMap.form.editTitle || "Editar Área"
          : translation.areaMap.form.title}
      </h3>

      {errors.length > 0 && (
        <Row type="flex" justify="center" align="middle">
          <label className="error">{translation.error.formError}</label>
        </Row>
      )}

      <Row gutter={[16, 16]}>
        <Col span={24}>
          <label className={errors.color ? "error" : ""}>
            {translation.areaMap.form.color} *
          </label>
          <Select
            placeholder={translation.areaMap.form.colorPlaceholder}
            value={values.color}
            onChange={handleColorChange}
            onBlur={handleBlur}
            style={{ width: "100%" }}
          >
            {COLOR_OPTIONS.map((color) => (
              <Option key={color.value} value={color.value}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <div
                    style={{
                      width: "16px",
                      height: "16px",
                      backgroundColor: color.value,
                      marginRight: "8px",
                      border: "1px solid #ccc",
                    }}
                  />
                  {translation.areaMap.colors[color.name]}
                </div>
              </Option>
            ))}
          </Select>
        </Col>

        <Col span={24}>
          <label className={errors.identificationCode ? "error" : ""}>
            {translation.areaMap.form.identificationCode} *
          </label>
          <Input
            name="identificationCode"
            value={values.identificationCode}
            maxLength={20}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={translation.areaMap.form.identificationCodePlaceholder}
            style={{ width: "100%" }}
          />
        </Col>

        <Col span={24}>
          <label className={errors.type ? "error" : ""}>
            {translation.areaMap.form.type} *
          </label>
          <Select
            placeholder={translation.areaMap.form.typePlaceholder}
            value={values.type}
            onChange={(value) => setFieldValue("type", value)}
            onBlur={handleBlur}
            style={{ width: "100%" }}
          >
            {AREA_TYPE_OPTIONS.map((option) => (
              <Option key={option.value} value={option.value}>
                {translation.areaMap.types[option.value]}
              </Option>
            ))}
          </Select>
        </Col>

        <Col span={24}>
          <label className={errors.retreatId ? "error" : ""}>
            {translation.areaMap.form.retreatSelection} (opcional)
          </label>
          <Select
            placeholder={translation.defaultSelectPlaceholder}
            value={values.retreatId || undefined}
            onChange={handleRetreatChange}
            onBlur={handleBlur}
            style={{ width: "100%" }}
            allowClear
            loading={isLoadingRetreats}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.props.children
                .toLowerCase()
                .indexOf(input.toLowerCase()) >= 0
            }
          >
            {retreats.map((retreat) => (
              <Option key={retreat.id} value={retreat.id}>
                {retreat.name}
              </Option>
            ))}
          </Select>
        </Col>

        <Col span={24}>
          <label className={errors.picketId ? "error" : ""}>
            {translation.areaMap.form.picketSelection} (opcional)
          </label>
          <Select
            placeholder={translation.defaultSelectPlaceholder}
            value={values.picketId || undefined}
            onChange={(value) => setFieldValue("picketId", value)}
            onBlur={handleBlur}
            style={{ width: "100%" }}
            allowClear
            loading={isLoadingPickets}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.props.children
                .toLowerCase()
                .indexOf(input.toLowerCase()) >= 0
            }
          >
            {filterPickets.map((picket) => (
              <Option key={picket.id} value={picket.id}>
                {picket.name}
              </Option>
            ))}
          </Select>
        </Col>

        <Col span={24}>
          <label className={errors.observation ? "error" : ""}>
            {translation.areaMap.form.observation} (opcional)
          </label>
          <TextArea
            name="observation"
            value={values.observation}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={translation.areaMap.form.observationPlaceholder}
            rows={3}
            style={{ width: "100%" }}
          />
        </Col>

        <Col span={24}>
          <label className={errors.area ? "error" : ""}>
            {translation.areaMap.form.area} *
          </label>
          <InputNumber
            name="area"
            value={area}
            placeholder={translation.areaMap.form.areaPlaceholder}
            min={0}
            step={0.01}
            precision={2}
            style={{ width: "100%" }}
            disabled
          />
          {touched.area && errors.area && (
            <div style={{ color: "red", fontSize: "12px" }}>{errors.area}</div>
          )}
        </Col>
      </Row>

      <Row gutter={16} style={{ marginTop: "24px" }}>
        <Col span={isEditMode ? 12 : 8}>
          <ButtonStandard
            buttonType="cancel"
            onClick={onCancel}
            style={{ width: "100%" }}
          >
            {translation.areaMap.form.cancel}
          </ButtonStandard>
        </Col>
        {!isEditMode && (
          <Col span={8}>
            <ButtonStandard
              buttonType="secondary"
              onClick={onRedrawPolygon}
              style={{ width: "100%" }}
            >
              {translation.areaMap.form.redraw || "Redesenhar"}
            </ButtonStandard>
          </Col>
        )}
        <Col span={isEditMode ? 12 : 8}>
          <ButtonStandard
            buttonType="principal"
            onClick={handleSubmit}
            disabled={isSaveButtonDisabled}
            style={{ width: "100%" }}
          >
            {isEditMode
              ? translation.areaMap.form.update || "Atualizar"
              : translation.areaMap.form.save}
          </ButtonStandard>
        </Col>
      </Row>
    </div>
  );
};

export default AreaForm;
